import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {ContextMenuOption} from 'components/ContextMenu';
import DataConnector from 'connect/DataConnector';
import deepCopy from 'lib/deepCopy';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import convertAssaySchemaToDataTableConfig, {
  convertFieldsToColumns,
} from 'notebook/data-tables/convertAssaySchemaToDataTableConfig';
import * as DataTableConfigActions from 'notebook/data-tables/DataTableConfigActions';
import {
  getColumnTypeFromSchema,
  getFieldsToDelete,
  getFieldTypeWarnings,
  getUnusedSchemaFieldIds,
  prepareDataTableConfigForStore,
} from 'notebook/data-tables/DataTableConfigEditUtil';
import {Section} from 'notebook/data-tables/DataTableConfigModalComponents';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import {DataTableConfig, FromServerDataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {
  convertDataTableConfigToTable,
  getInsertableColumnInfos,
  mergeTableUpdateIntoDataTableConfig,
} from 'notebook/data-tables/DataTableUtil';
import SetDataTableColumnTypeModal from 'notebook/data-tables/SetDataTableColumnTypeModal';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import SimpleTableAxisCell from 'notebook/tables/core-ui/SimpleTableAxisCell';
import excess from 'notebook/tables/excess';
import {SelectionRange, TableData, TableDimension} from 'notebook/tables/TableConstants';
import notifications from 'notifications';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {
  FIELD_TYPES_BY_TYPE_STRING,
  FieldType,
  SchemaField,
  TEXT_TYPE,
} from 'schemas/fields/SchemaFieldConstants';
import {getFieldForColumn} from 'schemas/fields/SchemaFieldGetters';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import ErrorList from 'ui-library/spec-editor/ErrorList';

interface BaseProps {
  assaySchema: AssaySchema;
  onRequestClose: () => void;
}

interface EnhancedProps {
  loaded: boolean;
  publishedConfig: FromServerDataTableConfig | undefined;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class DataTableConfigGenerateModalConnector extends DataConnector<BaseProps, EnhancedProps> {
  override stores = [DataTableConfigStore, SchemaFieldSelectorStore];

  enhanceProps(): EnhancedProps {
    const configs = this.dataTableConfigStore().getDataTableConfigs();
    const targetId = this.props.assaySchema.id;
    return {
      loaded: Boolean(configs),
      publishedConfig: configs
        ? // eslint-disable-next-line no-restricted-syntax
          _.find(configs, (config: FromServerDataTableConfig) => config.assaySchema.id === targetId)
        : undefined,
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(this.props.assaySchema.fields)
      ),
    };
  }
}

type DataTableConfigGenerateModalProps = BaseProps & EnhancedProps;

interface State {
  dataTableConfig: DataTableConfig | null;
  hiddenFieldIds: Array<number>;
  columnTypesByCid: {[cid: string]: FieldType};
  errors: Array<string> | null;
  errorMessage: string | null;
}

/**
 * A React component to (re)generate a DataTableConfig for an existing LegacyAssaySchema.
 */
export class DataTableConfigGenerateModal extends React.Component<DataTableConfigGenerateModalProps, State> {
  constructor(props: DataTableConfigGenerateModalProps) {
    super(props);
    const errors = null;
    const errorMessage = null;

    if (!props.loaded) {
      this.state = {
        dataTableConfig: null,
        columnTypesByCid: {},
        errors,
        errorMessage,
        hiddenFieldIds: [],
      };
    } else {
      this.state = {
        ...this._getStateFromProps(props),
        errors,
        errorMessage,
      };
    }
  }

  override componentDidMount(): void {
    // Re-fetch in case new configs were added.
    DataTableConfigActions.fetchAllDataTableConfigs();
  }

  override UNSAFE_componentWillReceiveProps(newProps: DataTableConfigGenerateModalProps): void {
    if (!isEqual(newProps, this.props)) {
      this.setState(this._getStateFromProps(newProps));
    }
  }

  _schemaHasPrimaryLinkField(): boolean {
    return Boolean(getPrimaryLinkField(this.props.assaySchema));
  }

  _getWarnings(
    assaySchema: AssaySchema,
    dataTableConfig: DataTableConfig,
    columnTypesByCid: {[cid: string]: FieldType},
    hiddenFieldIds: Array<number>
  ): Array<string> {
    const fieldsToDelete = getFieldsToDelete(assaySchema, dataTableConfig.columns, hiddenFieldIds);
    const fieldTypeWarnings = getFieldTypeWarnings(dataTableConfig, columnTypesByCid, assaySchema);
    return [
      ...fieldsToDelete.map(
        (field: SchemaField) => `Field "${field.displayName}" will be deleted from the schema.`
      ),
      ...fieldTypeWarnings,
    ];
  }

  _getStateFromProps(props: DataTableConfigGenerateModalProps): {
    dataTableConfig: DataTableConfig;
    hiddenFieldIds: Array<number>;
    columnTypesByCid: {};
  } {
    let dataTableConfig;
    let hiddenFieldIds: Array<number>;

    if (props.publishedConfig && props.publishedConfig.columns.length > 0) {
      const serverDataTableConfig = props.publishedConfig;
      dataTableConfig = deepCopy(serverDataTableConfig);
      prepareDataTableConfigForStore(dataTableConfig, {});

      hiddenFieldIds = getUnusedSchemaFieldIds(
        props.assaySchema,
        // prepareDataTableConfigForStore converted this to a DataTableConfig
        (dataTableConfig as unknown as DataTableConfig).columns
      );
    } else if (props.publishedConfig && props.publishedConfig.columns.length === 0) {
      const serverDataTableConfig = props.publishedConfig;
      dataTableConfig = deepCopy(serverDataTableConfig);
      prepareDataTableConfigForStore(dataTableConfig, {});

      dataTableConfig = {
        ...dataTableConfig,
        columns: convertFieldsToColumns(props.assaySchema, props.selectorsById),
      };

      hiddenFieldIds = getUnusedSchemaFieldIds(
        props.assaySchema,
        // prepareDataTableConfigForStore converted this to a DataTableConfig
        (dataTableConfig as unknown as DataTableConfig).columns
      );
    } else {
      // The Assay schema doesn't have a config, generate one
      dataTableConfig = convertAssaySchemaToDataTableConfig(props.assaySchema, props.selectorsById);
      hiddenFieldIds = [];
    }

    return {
      dataTableConfig: dataTableConfig as DataTableConfig,
      hiddenFieldIds,
      columnTypesByCid: {},
    };
  }

  _handlePublishErrors = ({errors, message}: {errors: Array<string>; message: string}): void => {
    this.setState({errors, errorMessage: message});
  };

  _handlePublish = (): void => {
    const numColumns = this.state.dataTableConfig?.columns.length ?? 0;
    if (numColumns === 0) {
      notifications.error('Please include at least one column in the table.');
      return;
    }

    DataTableConfigActions.publish(this.state.dataTableConfig!, {
      columnTypesByCid: this.state.columnTypesByCid,
      // eslint-disable-next-line no-restricted-syntax
      fieldIdsToDelete: _.pluck(
        getFieldsToDelete(
          this.props.assaySchema,
          this.state.dataTableConfig!.columns,
          this.state.hiddenFieldIds
        ),
        'id'
      ),
      onSuccess: this.props.onRequestClose,
      onError: this._handlePublishErrors,
    });
  };

  _getColumnTypesByCid(dataTableConfig: DataTableConfig): {[cid: string]: FieldType} {
    return _.object(
      dataTableConfig.columns.map((column) => [
        column.cid,
        this.state.columnTypesByCid[column.cid]
          ? this.state.columnTypesByCid[column.cid]
          : getColumnTypeFromSchema(column, this.props.assaySchema),
      ])
    );
  }

  _handleConfigTableUpdate = (index: number, {table}: {table: TableData}): void => {
    const dataTableConfig = mergeTableUpdateIntoDataTableConfig(this.state.dataTableConfig!, table);
    const columnTypesByCid = this._getColumnTypesByCid(dataTableConfig);

    const unusedSchemaFieldIds = getUnusedSchemaFieldIds(this.props.assaySchema, dataTableConfig.columns);
    const newHiddenFieldIds = this.state.hiddenFieldIds.filter((fieldId) =>
      unusedSchemaFieldIds.includes(fieldId)
    );

    this.setState({
      dataTableConfig,
      hiddenFieldIds: newHiddenFieldIds,
      columnTypesByCid,
    });
  };

  async _openEditFieldTypeModal(columnIndex: number): Promise<void> {
    const {dataTableConfig, columnTypesByCid} = this.state;
    const column = dataTableConfig!.columns[columnIndex];
    let fieldTypeString = columnTypesByCid[column.cid];
    if (fieldTypeString == null) {
      const field = getFieldForColumn(dataTableConfig!.assaySchema, column);
      if (field != null) {
        fieldTypeString = field.fieldType;
      }
    }

    const fieldType = fieldTypeString == null ? TEXT_TYPE : FIELD_TYPES_BY_TYPE_STRING[fieldTypeString];
    const result = await legacyRunModal(SetDataTableColumnTypeModal, {
      column,
      initialFieldType: fieldType,
      // Until we improve how the primary link field is represented, we can't allow creating a column
      // with a primary link field from the DataTableConfig unless the schema already has a
      // primary link field.  If the schema does not already have a primary link field,
      // then adding a column to the config with a primary link type will make that column
      // associated with the schema's primary link field, which is invalid.
      allowPrimaryLinkTypes: this._schemaHasPrimaryLinkField(),
    });
    if (result) {
      const newColumnTypesByCid = reactUpdate(columnTypesByCid, {
        [column.cid]: {$set: result.selectedFieldType.type},
      });
      this.setState({
        dataTableConfig,
        columnTypesByCid: newColumnTypesByCid,
      });
    }
  }

  _getHideColumnText(selectionRange: SelectionRange): string {
    if (selectionRange.minIndex !== selectionRange.maxIndex) {
      const [start, stop] = Array.from(
        [selectionRange.minIndex, selectionRange.maxIndex].map((value) =>
          excess.util.colNumberToLetters(value)
        )
      );
      return `Hide columns ${start} - ${stop}`;
    } else {
      return 'Hide column';
    }
  }

  _getFieldIdsToHide(selectionRange: SelectionRange, dataTableConfig: DataTableConfig): Array<number> {
    const columnsToHide = [];
    for (let i = selectionRange.minIndex; i <= selectionRange.maxIndex; i++) {
      const column = dataTableConfig.columns[i];
      if (column != null && column.tagSchemaFieldId != null) {
        columnsToHide.push(column.tagSchemaFieldId);
      }
    }
    return columnsToHide;
  }

  _handleHideColumns(selectionRange: SelectionRange): void {
    const {dataTableConfig} = this.state;

    const newHiddenFieldIds = [
      ...this.state.hiddenFieldIds,
      ...this._getFieldIdsToHide(selectionRange, dataTableConfig!),
    ];

    const newDataTableConfig = reactUpdate(dataTableConfig!, {
      columns: {
        $set: dataTableConfig!.columns.filter(
          (column, i) =>
            column.tagSchemaFieldId == null || !newHiddenFieldIds.includes(column.tagSchemaFieldId)
        ),
      },
    });
    const columnTypesByCid = this._getColumnTypesByCid(newDataTableConfig);

    this.setState({
      dataTableConfig: newDataTableConfig,
      columnTypesByCid,
      hiddenFieldIds: newHiddenFieldIds,
    });
  }

  _getExtraContextMenuOptions = (selectionRange: SelectionRange): Array<ContextMenuOption> | null => {
    const {dataTableConfig} = this.state;

    if (selectionRange == null) {
      return null;
    }
    const {minIndex, maxIndex} = selectionRange;

    const options = [];

    const numSelected = maxIndex - minIndex + 1;
    const numHideable = this._getFieldIdsToHide(selectionRange, dataTableConfig!).length;
    if (numSelected !== dataTableConfig!.columns.length && numSelected === numHideable) {
      options.push({
        key: 'hide-column-option',
        name: this._getHideColumnText(selectionRange),
        click: () => {
          this._handleHideColumns(selectionRange);
        },
      });
    }

    if (numSelected === 1) {
      options.push({
        key: 'field-type-option',
        name: 'Edit type...',
        click: () => this._openEditFieldTypeModal(minIndex),
      });
    }

    return options;
  };

  renderBody(): JSX.Element {
    if (!this.props.loaded) {
      return (
        <div className="text-center">
          <HorizontalThrobber />
        </div>
      );
    }

    const {dataTableConfig, errorMessage, errors, columnTypesByCid, hiddenFieldIds} = this.state;
    const warnings = this._getWarnings(
      this.props.assaySchema,
      dataTableConfig!,
      columnTypesByCid,
      hiddenFieldIds
    );

    const table = convertDataTableConfigToTable(dataTableConfig!, false /* indicateCustomColumns */, {
      alwaysAllowColumnRename: true,
      columnTypesByCid,
    });
    const plf = getPrimaryLinkField(this.props.assaySchema);
    if (BenchlingConfig.ELN_ENABLE_INSERT_NEW_RESULTS_TABLE && plf != null) {
      table.rowLabels = [plf.displayName];
      table._rowHeader = (
        <SimpleTableAxisCell
          index={0}
          dim={TableDimension.COLUMN}
          label={plf.displayName}
          isLeftAlign={false}
          numDims={2}
        />
      );
    }
    return (
      <div>
        {dataTableConfig && (
          <Section title="">
            <div className="modal-mediocre-tableWrapper">
              <SimpleTableEditable
                {...standaloneProps}
                table={table}
                cid="modal-table-new"
                locked={false}
                viewer={false}
                hideFormulaBox={true}
                icon="table"
                insertableColumnInfos={getInsertableColumnInfos(dataTableConfig, this.props.selectorsById)}
                enableFormulas={false}
                hasPlaceholderRows={true}
                storeState={() => {}}
                tableLabel={MODEL_NAMES.Result}
                updateHandler={this._handleConfigTableUpdate}
                isLeftAlignColumnHeader={true}
                getExtraContextMenuOptions={this._getExtraContextMenuOptions}
              />
            </div>
          </Section>
        )}
        {warnings.length > 0 && (
          <Section title="The following warnings were encountered:">
            {warnings.map((warning, i) => (
              <div key={i}>
                <BenchlingIcon icon="warning" className="u-dark-warning-color u-tiny-spacing-right" />
                {warning}
              </div>
            ))}
          </Section>
        )}
        {((errors != null && errors.length > 0) || errorMessage != null) && (
          <Section title="The following errors were encountered:">
            <ErrorList errors={errors} errorMessage={errorMessage} key="errors" />
          </Section>
        )}
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <Modal
        title={`Configure ${MODEL_NAMES.Result_Table}`}
        closeableOnEscapeKey={false}
        fullWidth={true}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this._handlePublish}
        className="mediocre"
        buttons={[
          {
            text: 'Publish',
            submit: true,
            style: ButtonStyle.PRIMARY,
            disabled: !this.props.loaded,
          },
        ]}
      >
        {this.renderBody()}
      </Modal>
    );
  }
}

export default DataTableConfigGenerateModalConnector.connect(DataTableConfigGenerateModal);
